<div class="relative flex h-full flex-col">
  <app-loader [loadingState]="loadingState$ | async" [relativeWidthAndHeight]="true"></app-loader>

  <div
    [ngClass]="{
      'bg-gradient-to-br from-bright-yellow/35 via-white/90 to-white':
        hasDuplicateFilters && !searchActive && (loadingState$ | async) === 'loaded',
    }"
  >
    <app-header
      [title]="
        hasDuplicateFilters && !searchActive && (groups$ | async)?.length > 0
          ? 'Potential customer matches found'
          : 'Link or create customer'
      "
    >
      @if (hasDuplicateFilters && !searchActive && (groups$ | async)?.length > 0) {
        <ion-icon first-slot name="warning" class="mr-2 size-8 text-bright-yellow"></ion-icon>
      }
      @if (hasDuplicateFilters && !searchActive && (groups$ | async)?.length > 0) {
        <div below-title-slot class="ml-10 -mt-0.5 mb-1.5 text-xs text-black/70">
          Potential matches are highlighted below. Select one to link it, or create a new customer.
        </div>
      } @else {
        <div below-title-slot class="-mt-0.5 mb-1.5 text-xs text-black/70">
          Search for an existing customer or create a new one from this lead.
        </div>
      }
      <button
        additional-slot
        id="close-modal-button"
        class="btn-primary m-0 flex w-min items-center justify-center p-1"
        (click)="closeModal()"
      >
        <ion-icon class="h-5 w-5" name="close-outline"></ion-icon>
      </button>
    </app-header>
  </div>

  <div class="flex min-h-0 flex-1 flex-col bg-grey-light-concrete/50">
    @if (searchActive) {
      <div
        class="flex items-center gap-2 border-y border-grey-concrete bg-grey-light-concrete p-3"
        [formGroup]="searchForm"
      >
        <div
          id="search-container"
          class="my-2 flex min-w-0 flex-1 cursor-text items-center justify-between overflow-hidden rounded-2xl border border-solid border-grey-concrete bg-white shadow ring-1 ring-white/0 transition focus-within:border-primary focus-within:ring-primary"
        >
          <input
            id="customer-search-input"
            class="h-min min-w-0 flex-1 border-none bg-transparent px-4 py-1 text-lg shadow-none placeholder:italic placeholder:text-base hover:border-none"
            type="text"
            placeholder="Search customers by name, email, or ID"
            autocomplete="off"
            formControlName="search"
            [attr.readonly]="(loadingState$ | async) === 'loading' ? true : null"
          />
          <ion-icon class="p-2 text-xl text-secondary" name="search-outline"></ion-icon>
        </div>
        @if ((permissions$ | async)?.includes('customers_upsert')) {
          <div class="relative mx-4 my-auto w-10 max-sm:hidden">
            <div
              class="absolute left-1/2 top-1/2 h-16 w-1 -translate-x-1/2 -translate-y-1/2 border-r border-grey-mid/75"
            ></div>
            <div class="absolute inset-0 flex items-center justify-center">
              <p
                class="text-base text-grey-blue/80 bg-grey-light-concrete leading-6 text-center pl-0.5 text-empty-field"
              >
                or
              </p>
            </div>
          </div>
          <button
            id="create-customer-from-lead-search"
            class="btn-primary flex shrink-0 items-center gap-2 text-sm"
            (click)="createCustomer()"
          >
            <ion-icon name="person-add-outline" class="size-4"></ion-icon>
            Create customer
          </button>
        }
      </div>
    }

    <div class="min-h-0 flex-1 overflow-auto p-3">
      @if ((groups$ | async)?.length > 0) {
        @if (hasDuplicateFilters && !searchActive) {
          <p class="mb-3 text-center text-xs text-grey-blue/75 font-normal">
            Select an existing customer record to link to this lead
          </p>
        }
        <div class="grid grid-cols-1 gap-2">
          @for (group of groups$ | async; track group.id) {
            @if (!searchActive) {
              <app-lead-customer-selector-item
                [group]="group"
                [groupFilters]="groupFilters"
                [selected]="selectedGroupToConfirm?.attributes.id === group.attributes.id"
                (selectedGroup)="selectGroup($event)"
              ></app-lead-customer-selector-item>
            } @else {
              <div
                role="button"
                tabindex="0"
                class="group flex cursor-pointer flex-col rounded-md border border-grey-concrete bg-white p-3 text-left text-xs shadow-sm transition-colors"
                [ngClass]="{
                  'is-selected border-primary/60 bg-primary/10 ring-1 ring-primary':
                    selectedGroupToConfirm?.attributes.id === group.attributes.id,
                  'hover:bg-grey-concrete/40': selectedGroupToConfirm?.attributes.id !== group.attributes.id,
                }"
                (click)="selectGroup(group)"
                (keydown.enter)="selectGroup(group)"
                (keydown.space)="selectGroup(group)"
              >
                <div class="flex items-start gap-2">
                  <div
                    class="flex size-8 shrink-0 items-center justify-center rounded"
                    [ngClass]="
                      group.attributes.group_entity_type === 'person' ? 'bg-turquoise-bright/15' : 'bg-blue-dark/15'
                    "
                  >
                    <ion-icon
                      [name]="group.attributes.group_entity_type === 'person' ? 'person-outline' : 'business-outline'"
                      class="size-5 mt-0.5"
                      [ngClass]="
                        group.attributes.group_entity_type === 'person' ? 'text-turquoise-bright' : 'text-blue-dark'
                      "
                    ></ion-icon>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="font-medium text-sm">{{ group.attributes.name }}</div>
                    <a
                      class="btn-link font-normal text-[.9em]/none"
                      [routerLink]="['/customers/edit/', group.attributes.id]"
                      (click)="$event.stopPropagation()"
                    >
                      {{ group.attributes.id }}
                    </a>
                  </div>
                  <div class="pt-0.5">
                    <ion-checkbox
                      mode="md"
                      class="pointer-events-none"
                      [checked]="selectedGroupToConfirm?.attributes.id === group.attributes.id"
                    ></ion-checkbox>
                  </div>
                </div>
                <div class="grid md:grid-cols-2">
                  @if (group.attributes.parent_group_id) {
                    <div class="mt-3">
                      <label class="p-0">Parent</label>
                      <div class="mt-0.5">
                        <app-group-display
                          [groupId]="group.attributes.parent_group_id"
                          [groupName]="group.attributes.parent_group_name || ''"
                          [enableLink]="true"
                        ></app-group-display>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          }
        </div>
      } @else if ((loadingState$ | async) === 'loaded' && searchForm.valid) {
        <div class="empty-state px-10 py-32 shadow-none">
          <h1><ion-icon class="size-14" name="people"></ion-icon></h1>
          <h2 class="text-2xl">No customers found</h2>
          <p class="text-sm">Try another customer name, email address, or customer ID.</p>
        </div>
      }
    </div>

    @if (
      !searchActive && hasDuplicateFilters && (loadingState$ | async) === 'loaded' && (groups$ | async)?.length > 0
    ) {
      <div
        class="flex flex-wrap items-center justify-between gap-2 border-t border-grey-concrete bg-white p-3 text-sm text-grey-blue shadow-md"
      >
        <button
          id="continue-create-or-link-customer"
          class="btn-default flex items-center gap-2 text-sm"
          (click)="openSearch()"
        >
          Ignore & Continue
          <ion-icon name="arrow-forward-outline" class="size-4"></ion-icon>
        </button>
        <div class="flex min-w-0 items-center gap-2">
          <button
            id="confirm-duplicate-group"
            class="btn-primary text-sm"
            [class.hidden]="!selectedGroupToConfirm"
            (click)="confirmSelectedGroup()"
          >
            Link customer
          </button>
        </div>
      </div>
    } @else if (searchActive) {
      <div
        class="flex fixed w-full bottom-0 items-center justify-end gap-2 border-t border-grey-concrete bg-white/50 p-3 text-sm text-grey-blue shadow-md backdrop-blur-sm"
        [class.hidden]="!selectedGroupToConfirm"
      >
        <button id="confirm-search-group" class="btn-primary text-sm" (click)="confirmSelectedGroup()">
          Link Customer
        </button>
      </div>
    }
  </div>
</div>
